import NestedAccordion from "../../../components/NestedAccordion";
import FileUploadField from "@/components/ui/FileUploadField";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  FileDetails,
  RecordingStageAdminRequiredActions,
  TVideoSetup,
} from "@/types/projectType";

import { FileCard } from "../../../components/FileCard";
import { RevisionFileCard } from "../../../components/RevisionFileCard";
import { getTimeDifference } from "@/utils/getTimeDifference";
import useOrderVideoParams from "@/hooks/useOrderVideoParams";
import {
  useCreateSetupMutation,
  useUploadNewSetupMutation,
} from "@/redux/feature/orderManagement/VideoSetup/videoSetupApi";
import { useGetDriveLinkQuery } from "@/redux/feature/driveLink/driveLinkApi";
import { useGlobalContext } from "@/context/GlobalContext";

const VideoSetup = ({
  videoSetupData,
  isRawFootageExists,
  ActionsRequired,
  projectId,
}: {
  projectId: string;
  videoSetupData: TVideoSetup;
  ActionsRequired?: RecordingStageAdminRequiredActions[];
  isRawFootageExists: boolean;
}) => {
  const isActionRequired = ActionsRequired?.find(
    (item) =>
      item?.isVideoSetupUploaded === false ||
      item.isRevisedVideoSetupUploaded === false
  );

  const { orderId, videoId } = useOrderVideoParams();
  const timeLine = getTimeDifference(videoSetupData?.adminTimeLine);

  const [clearFiles, setClearFiles] = useState(false);
  const [setupFiles, setSetupFiles] = useState<FileDetails[]>([]);

  const { userData } = useGlobalContext();

  // Fetch Drive upload folder for this creator/project/video
  const { data } = useGetDriveLinkQuery({
    email: userData?.email,
    projectId,
    videoId: videoId as string,
    type: "video_setup",
  });

  const driveLink = data?.data?.drive_link;

  const [createSetup, { isLoading: UploadingSetupLoading }] =
    useCreateSetupMutation();
  const [uploadNewSetup, { isLoading: RevisedUploadingSetupLoading }] =
    useUploadNewSetupMutation();

  const handleUploadSetup = async () => {
    if (!orderId || !videoId) return;

    const isUploadingWithDriveLink = setupFiles.length === 0;

    // CASE: Revision upload
    if (videoSetupData?.requiredActions?.requiresRevision) {
      const payload = {
        orderId,
        videoId,
        data: {
          newSetupFile: setupFiles[0], // undefined if using drive
          description: "Revised Setup From Creator",
          isUploadingWithDriveLink,
          driveLink,
        },
      };

      try {
        const res = await uploadNewSetup(payload).unwrap();
        if (res.success) {
          toast.success("Setup uploaded successfully!");
          setSetupFiles([]);
          setClearFiles(true);
        } else {
          toast.error(res.message || "Failed to upload setup");
        }
      } catch (err) {
        console.log(err);
      }
      return;
    }

    // CASE: First time setup upload
    const payload = {
      order_id: orderId,
      video_id: videoId,
      isUploadingWithDriveLink,
      driveLink,
      videoSetupVersions: [
        {
          versionNumber: 1,
          uploadedBy: "admin",
          description: "First version of video setup",
          file: setupFiles, // empty array when drive used
          uploadedAt: new Date(),
        },
      ],
    };

    try {
      const res = await createSetup(payload).unwrap();
      if (res.success) {
        toast.success("Setup uploaded successfully!");
        setSetupFiles([]);
        setClearFiles(true);
      } else {
        toast.error(res.message || "Failed to upload setup");
      }
    } catch (err) {
      console.log(err);
    }
  };

  const currentVideoSetup = videoSetupData?.videoSetupVersions?.slice(-1)[0];
  const { inProgress = false, isApproved = false } =
    videoSetupData?.status || {};
  const { requiresRevision = false, requiresApproval = false } =
    videoSetupData?.requiredActions || {};

  const [isOpen, setIsOpen] = useState(false);

  const Status =
    requiresRevision === true
      ? "Needs Revision"
      : requiresApproval === true
      ? "Not Approved Yet"
      : inProgress === true
      ? "In Progress"
      : isApproved === true
      ? "Approved"
      : "";

  useEffect(() => {
    if (
      isActionRequired?.isVideoSetupUploaded === false ||
      isActionRequired?.isRevisedVideoSetupUploaded === false
    ) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [
    isActionRequired?.isVideoSetupUploaded,
    isActionRequired?.isRevisedVideoSetupUploaded,
  ]);

  const showAllVersion = !isApproved && !requiresRevision;

  return (
    <NestedAccordion
      isApproved={isApproved}
      initiallyOpen={videoSetupData?.status?.inProgress || requiresRevision}
      title="Video Setup"
      status={Status}
      isOpen={isOpen}
      actionRequired={
        isActionRequired?.isVideoSetupUploaded === false ||
        isActionRequired?.isRevisedVideoSetupUploaded === false
      }
      setIsOpen={setIsOpen}
      timeLine={timeLine}
      initiated={videoSetupData ? true : false}
    >
      <div className="p-3">
        {!isApproved && !requiresApproval && (
          <div className="rounded-[10px] space-y-4">
            <FileUploadField
              clearFiles={clearFiles}
              onFilesUploaded={(file) => setSetupFiles(file)}
              maxFiles={1}
            />

            <p className="text-center py-2">or</p>

            {/* Google Drive upload option */}
            <div className="w-full rounded-lg border bg-gray-50 flex flex-col items-center justify-center py-6 p-4">
              <p className="text-gray-700 text-sm mb-2">
                Upload your file to Google Drive
              </p>

              <a
                href={driveLink}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-blue-500 font-medium text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition"
              >
                Upload to Google Drive
              </a>
            </div>
          </div>
        )}

        {isApproved && (
          <>
            <p className="text-[13px] font-medium mt-4">Final Version</p>
            <FileCard
              active={true}
              file={currentVideoSetup?.file[0]}
              isApproved={isApproved}
            />
          </>
        )}

        {requiresRevision && (
          <>
            <p className="text-[13px] font-medium mt-4">
              Request Revisions ({videoSetupData?.videoSetupVersions?.length})
            </p>
            <RevisionFileCard
              active={true}
              file={currentVideoSetup?.file[0]}
              comment={currentVideoSetup?.revisionComment as string}
            />
          </>
        )}

        {/* Show previous versions */}
        <div>
          {videoSetupData?.videoSetupVersions
            ?.slice(0, showAllVersion ? undefined : -1)
            ?.reverse().length > 0 && (
            <div>
              <p className="text-[13px] font-medium mt-4">Previous Version</p>
              {videoSetupData?.videoSetupVersions
                ?.slice(0, showAllVersion ? undefined : -1)
                ?.reverse()
                .map((item, i) => (
                  <FileCard
                    key={i}
                    requiresRevision={false}
                    active={false}
                    file={item?.file[0]}
                  />
                ))}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="bg-[#F5F9FF] p-3 rounded-bl-xl rounded-br-xl flex justify-between items-center">
        <p className="text-sm text-[#344054] font-medium">Cancel</p>
        <button
          onClick={handleUploadSetup}
          disabled={
            RevisedUploadingSetupLoading ||
            UploadingSetupLoading ||
            (setupFiles.length === 0 && !driveLink) // must have file OR drive link
          }
          className="text-white disabled:opacity-50 text-sm px-3 py-2 font-semibold rounded-lg bg-[#17457D]"
        >
          {RevisedUploadingSetupLoading || UploadingSetupLoading
            ? "Loading..."
            : "Submit upload"}
        </button>
      </div>
    </NestedAccordion>
  );
};

export default VideoSetup;

// import NestedAccordion from "../../../components/NestedAccordion";
// import FileUploadField from "@/components/ui/FileUploadField";
// import { useEffect, useState } from "react";
// import toast from "react-hot-toast";
// import {
//   FileDetails,
//   RecordingStageAdminRequiredActions,
//   TVideoSetup,
// } from "@/types/projectType";

// import { FileCard } from "../../../components/FileCard";
// import { RevisionFileCard } from "../../../components/RevisionFileCard";
// import { getTimeDifference } from "@/utils/getTimeDifference";
// import useOrderVideoParams from "@/hooks/useOrderVideoParams";
// import {
//   useCreateSetupMutation,
//   useUploadNewSetupMutation,
// } from "@/redux/feature/orderManagement/VideoSetup/videoSetupApi";
// import HaveMoreToShow from "./components/HaveMoreToShow";

// const VideoSetup = ({
//   videoSetupData,
//   isRawFootageExists,
//   ActionsRequired,
// }: {
//   videoSetupData: TVideoSetup;
//   ActionsRequired?: RecordingStageAdminRequiredActions[];
//   isRawFootageExists: boolean;
// }) => {
//   const isActionRequired = ActionsRequired?.find(
//     (item) =>
//       item?.isVideoSetupUploaded === false ||
//       item.isRevisedVideoSetupUploaded === false
//   );

//   //  Grabbing important params
//   const { orderId, videoId } = useOrderVideoParams();
//   const timeLine = getTimeDifference(videoSetupData?.adminTimeLine);
//   const [clearFiles, setClearFiles] = useState(false);
//   const [setupFiles, setSetupFiles] = useState<FileDetails[]>([]);

//   const [createSetup, { isLoading: UploadingSetupLoading }] =
//     useCreateSetupMutation();
//   const [uploadNewSetup, { isLoading: RevisedUploadingSetupLoading }] =
//     useUploadNewSetupMutation();

//   const handleUploadSetup = async () => {
//     if (setupFiles.length === 0) {
//       return;
//     }

//     if (videoSetupData?.requiredActions?.requiresRevision) {
//       const payload = {
//         orderId,
//         videoId,
//         data: {
//           description: "Revised Footage From Admin",
//           newSetupFile: setupFiles[0],
//         },
//       };
//       try {
//         const res = await uploadNewSetup(payload).unwrap();
//         if (res.success) {
//           toast.success("Setup Uploaded successfully!");
//           // setOpen(sfalse);
//           setSetupFiles([]);
//           setClearFiles(true);
//         } else {
//           toast.error(res.message || "Failed to Upload  Setup ");
//         }
//       } catch (err) {
//         console.log(err);
//       }
//     } else {
//       const payload = {
//         order_id: orderId,
//         video_id: videoId,
//         videoSetupVersions: [
//           {
//             versionNumber: 1,
//             uploadedBy: "admin",
//             description: "First version of raw footage",
//             file: setupFiles,
//             uploadedAt: new Date(),
//           },
//         ],
//       };
//       try {
//         const res = await createSetup(payload).unwrap();
//         if (res.success) {
//           toast.success("Set up Uploaded successfully!");
//           // setOpen(sfalse);
//           setSetupFiles([]);
//           setClearFiles(true);
//         } else {
//           toast.error(res.message || "Failed to Upload Set up ");
//         }
//       } catch (err) {
//         console.log(err);
//       }
//     }
//   };

//   const currentVideoSetup = videoSetupData?.videoSetupVersions?.slice(-1)[0];
//   const { inProgress = false, isApproved = false } =
//     videoSetupData?.status || {};
//   const { requiresRevision = false, requiresApproval = false } =
//     videoSetupData?.requiredActions || {};

//   const [isOpen, setIsOpen] = useState(false);

//   // Determine the status based on scriptData
//   const Status =
//     requiresRevision === true
//       ? "Needs Revision"
//       : requiresApproval === true
//       ? "Not Approved Yet"
//       : inProgress === true
//       ? "In Progress"
//       : isApproved === true
//       ? "Approved"
//       : "";

//   useEffect(() => {
//     // if (isRawFootageExists) {
//     if (
//       isActionRequired?.isVideoSetupUploaded === false ||
//       isActionRequired?.isRevisedVideoSetupUploaded === false
//     ) {
//       setIsOpen(true);
//     } else {
//       setIsOpen(false);
//     }

//     // }
//   }, [
//     isActionRequired?.isVideoSetupUploaded,
//     isActionRequired?.isRevisedVideoSetupUploaded,
//   ]);
//   // console.log("videosetup", videoSetupData?.status?.inProgress);

//   const showAllVersion = !isApproved && !requiresRevision;
//   return (
//     <NestedAccordion
//       isApproved={isApproved}
//       initiallyOpen={videoSetupData?.status?.inProgress || requiresRevision}
//       title="Video Setup"
//       status={Status}
//       isOpen={isOpen}
//       actionRequired={
//         isActionRequired?.isVideoSetupUploaded === false ||
//         isActionRequired?.isRevisedVideoSetupUploaded === false
//       }
//       setIsOpen={setIsOpen}
//       timeLine={timeLine}
//       initiated={videoSetupData ? true : false}
//     >
//       {/* Main Content */}
//       <div className="p-3">
//         {/* showing file input and and the final selected file */}
//         {!isApproved && !requiresApproval && (
//           <div>
//             <div className="rounded-[10px]">
//               <FileUploadField
//                 clearFiles={clearFiles}
//                 onFilesUploaded={(file) => setSetupFiles(file)}
//                 maxFiles={1}
//               />
//             </div>
//           </div>
//         )}
//         {isApproved && (
//           <>
//             <p className="text-[13px] font-medium mt-4">Final Version</p>
//             <FileCard
//               active={true}
//               file={currentVideoSetup?.file[0]}
//               isApproved={isApproved}
//             />
//           </>
//         )}

//         {/* Toogle button for showing request extension modal */}
//         {/* {requiresRevision && !isApproved && currentVideoSetup && (
//           <div className="flex border-b border-[#D0D5DD] py-3 items-center gap-x-4">
//             <button
//               onClick={() => toggleExtensionModal(true)}
//               className="text-[#ED5E00] text-[14px] font-medium"
//             >
//               Request an extension
//             </button>
//           </div>
//         )} */}

//         {/* Show that file that needs revision*/}
//         {requiresRevision && (
//           <>
//             <p className="text-[13px] font-medium mt-4">
//               Request Revisions ({videoSetupData?.videoSetupVersions?.length})
//             </p>
//             <RevisionFileCard
//               active={true}
//               file={currentVideoSetup?.file[0]}
//               comment={currentVideoSetup?.revisionComment as string}
//             />
//           </>
//         )}

//         {/* Showing all versions */}
//         <div>
//           {videoSetupData?.videoSetupVersions
//             ?.slice(0, showAllVersion ? undefined : -1)
//             ?.reverse().length > 0 && (
//             <div>
//               <p className="text-[13px] font-medium mt-4">Previous Version</p>
//               {videoSetupData?.videoSetupVersions
//                 ?.slice(0, showAllVersion ? undefined : -1)
//                 ?.reverse()
//                 .map((item, i) => (
//                   <FileCard
//                     requiresRevision={false}
//                     key={i}
//                     active={false}
//                     file={item?.file[0]}
//                   />
//                 ))}
//             </div>
//           )}
//         </div>
//       </div>

//       <div className="bg-[#F5F9FF] p-3 rounded-bl-xl rounded-br-xl flex justify-between items-center">
//         <p className="text-sm text-[#344054] font-medium">Cancel</p>
//         <button
//           onClick={handleUploadSetup}
//           disabled={
//             RevisedUploadingSetupLoading ||
//             UploadingSetupLoading ||
//             setupFiles.length === 0
//           }
//           className="text-white disabled:opacity-50 text-sm px-3 py-2 font-semibold rounded-lg bg-[#17457D]"
//         >
//           {RevisedUploadingSetupLoading || UploadingSetupLoading
//             ? "Loading..."
//             : "Submit upload"}
//         </button>
//       </div>

//     </NestedAccordion>
//   );
// };

// export default VideoSetup;
